import { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect, useRef } from 'react'
import { format, startOfMonth, endOfMonth } from 'date-fns'

export interface FilterState {
  dataInicio: string
  dataFim: string
  tipoNegocio: 'global' | 'realiza' | 'invest'
  idEmpreendimento: number | null
  idCorretor: number | null
  idImobiliaria: number | null
}

interface FilterOption {
  value: number
  label: string
}

interface CorretorOption extends FilterOption {
  idImobiliaria: number | null
}

interface FilterOptions {
  empreendimentos: FilterOption[]
  corretores: FilterOption[]
  corretoresCompletos: CorretorOption[]
  imobiliarias: FilterOption[]
}

interface FilterContextType {
  filters: FilterState
  filterOptions: FilterOptions
  setDataInicio: (date: string) => void
  setDataFim: (date: string) => void
  setTipoNegocio: (tipo: 'global' | 'realiza' | 'invest') => void
  setIdEmpreendimento: (id: number | null) => void
  setIdCorretor: (id: number | null) => void
  setIdImobiliaria: (id: number | null) => void
  clearFilters: () => void
  queryParams: string
  isLoading: boolean
  filtersVersion: number
}

const defaultFilters: FilterState = {
  dataInicio: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
  dataFim: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  tipoNegocio: 'global',
  idEmpreendimento: null,
  idCorretor: null,
  idImobiliaria: null,
}

const FilterContext = createContext<FilterContextType | undefined>(undefined)

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001'

// Mapeamento de nomes de imobiliárias internas
const IMOBILIARIAS_INTERNAS: Record<string, string> = {
  'UTANI - EXTERNOS': 'UTANI',
  'FAJ HOUSE - ALFA': 'SDR',
}

// IDs das imobiliárias internas (para ordenação)
const IDS_IMOBILIARIAS_INTERNAS = [109, 4] // UTANI, SDR

export function FilterProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<FilterState>(defaultFilters)
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    empreendimentos: [],
    corretores: [],
    corretoresCompletos: [],
    imobiliarias: [],
  })
  const [allEmpreendimentos, setAllEmpreendimentos] = useState<FilterOption[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filtersVersion, setFiltersVersion] = useState(0)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Função para renomear imobiliárias internas
  const formatImobiliariaName = (nome: string): string => {
    return IMOBILIARIAS_INTERNAS[nome] || nome
  }

  // Função para ordenar imobiliárias (internas primeiro)
  const sortImobiliarias = (imobs: FilterOption[]): FilterOption[] => {
    return [...imobs].sort((a, b) => {
      const aIsInternal = IDS_IMOBILIARIAS_INTERNAS.includes(a.value)
      const bIsInternal = IDS_IMOBILIARIAS_INTERNAS.includes(b.value)
      if (aIsInternal && !bIsInternal) return -1
      if (!aIsInternal && bIsInternal) return 1
      return a.label.localeCompare(b.label)
    })
  }

  // Carregar opções de filtros do backend (uma vez)
  useEffect(() => {
    const loadFilterOptions = async () => {
      setIsLoading(true)
      try {
        const [empRes, corrRes, imobRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/empreendimentos/lista`),
          fetch(`${API_BASE_URL}/api/corretores/lista`),
          fetch(`${API_BASE_URL}/api/corretores/imobiliarias`),
        ])

        const [empreendimentos, corretores, imobiliarias] = await Promise.all([
          empRes.ok ? empRes.json() : [],
          corrRes.ok ? corrRes.json() : [],
          imobRes.ok ? imobRes.json() : [],
        ])

        const corretoresCompletos = corretores.map((c: any) => ({
          value: c.id || c.idcorretor,
          label: c.nome || c.corretor,
          idImobiliaria: c.idimobiliaria || c.id_imobiliaria || null,
        }))

        const empList = empreendimentos.map((e: any) => ({
          value: e.id || e.idempreendimento,
          label: e.nome || e.empreendimento,
        }))

        // Formata e ordena imobiliárias
        const imobList = sortImobiliarias(
          imobiliarias.map((i: any) => ({
            value: i.id || i.idimobiliaria,
            label: formatImobiliariaName(i.nome || i.imobiliaria),
          }))
        )

        setAllEmpreendimentos(empList)
        setFilterOptions({
          empreendimentos: empList,
          corretores: corretoresCompletos.map((c) => ({
            value: c.value,
            label: c.label,
          })),
          corretoresCompletos,
          imobiliarias: imobList,
        })
      } catch (error) {
        console.error('Erro ao carregar opcoes de filtros:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadFilterOptions()
  }, [])

  // Debounce para incrementar a versão dos filtros
  const incrementVersion = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      setFiltersVersion((v) => v + 1)
    }, 300) // 300ms de debounce
  }, [])

  const setDataInicio = useCallback((date: string) => {
    setFilters((prev) => ({ ...prev, dataInicio: date }))
    incrementVersion()
  }, [incrementVersion])

  const setDataFim = useCallback((date: string) => {
    setFilters((prev) => ({ ...prev, dataFim: date }))
    incrementVersion()
  }, [incrementVersion])

  const setTipoNegocio = useCallback((tipo: 'global' | 'realiza' | 'invest') => {
    setFilters((prev) => ({ ...prev, tipoNegocio: tipo, idEmpreendimento: null }))
    incrementVersion()
  }, [incrementVersion])

  // Atualiza lista de empreendimentos quando tipo_negocio muda
  useEffect(() => {
    const loadEmpreendimentosByTipo = async () => {
      if (filters.tipoNegocio === 'global') {
        setFilterOptions(prev => ({ ...prev, empreendimentos: allEmpreendimentos }))
        return
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/empreendimentos/lista?tipo_negocio=${filters.tipoNegocio}`)
        if (res.ok) {
          const empreendimentos = await res.json()
          setFilterOptions(prev => ({
            ...prev,
            empreendimentos: empreendimentos.map((e: any) => ({
              value: e.id || e.idempreendimento,
              label: e.nome || e.empreendimento,
            }))
          }))
        }
      } catch (error) {
        console.error('Erro ao carregar empreendimentos:', error)
      }
    }

    if (allEmpreendimentos.length > 0) {
      loadEmpreendimentosByTipo()
    }
  }, [filters.tipoNegocio, allEmpreendimentos])

  const setIdEmpreendimento = useCallback((id: number | null) => {
    setFilters((prev) => ({ ...prev, idEmpreendimento: id }))
    incrementVersion()
  }, [incrementVersion])

  const setIdCorretor = useCallback((id: number | null) => {
    setFilters((prev) => ({ ...prev, idCorretor: id }))
    incrementVersion()
  }, [incrementVersion])

  const setIdImobiliaria = useCallback((id: number | null) => {
    setFilters((prev) => {
      // Se mudou a imobiliaria, limpa o corretor selecionado
      const newState = { ...prev, idImobiliaria: id }
      if (id !== null && prev.idCorretor !== null) {
        // Verifica se o corretor pertence a nova imobiliaria
        const corretor = filterOptions.corretoresCompletos.find(c => c.value === prev.idCorretor)
        if (corretor && corretor.idImobiliaria !== id) {
          newState.idCorretor = null
        }
      }
      return newState
    })
    incrementVersion()
  }, [incrementVersion, filterOptions.corretoresCompletos])

  const clearFilters = useCallback(() => {
    setFilters(defaultFilters)
    incrementVersion()
  }, [incrementVersion])

  // Memoizar a string de query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    params.append('data_inicio', filters.dataInicio)
    params.append('data_fim', filters.dataFim)
    params.append('tipo_negocio', filters.tipoNegocio)

    if (filters.idEmpreendimento) {
      params.append('id_empreendimento', filters.idEmpreendimento.toString())
    }
    if (filters.idCorretor) {
      params.append('id_corretor', filters.idCorretor.toString())
    }
    if (filters.idImobiliaria) {
      params.append('id_imobiliaria', filters.idImobiliaria.toString())
    }

    return params.toString()
  }, [filters])

  // Cleanup do debounce
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return (
    <FilterContext.Provider
      value={{
        filters,
        filterOptions,
        setDataInicio,
        setDataFim,
        setTipoNegocio,
        setIdEmpreendimento,
        setIdCorretor,
        setIdImobiliaria,
        clearFilters,
        queryParams,
        isLoading,
        filtersVersion,
      }}
    >
      {children}
    </FilterContext.Provider>
  )
}

export function useFilters() {
  const context = useContext(FilterContext)
  if (context === undefined) {
    throw new Error('useFilters deve ser usado dentro de um FilterProvider')
  }
  return context
}
